export interface PendingHoliday {
    name?: string;
    startDate?: string;  
    endDate?: string;     
    description?: string;
    status?: string;      
    holidayType?: string; 
}


export interface PendingDeleteHoliday {
    awaitingName?: boolean;
}

export const defaultHolidayDate = ""; 


// -------------------------------
// CREATE HOLIDAY — START
// -------------------------------
export const startCreateHolidayFlow = (
    setPendingHoliday: (data: PendingHoliday | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingHoliday({ name: nameFromIntent});

        pushAIChat(`Great! What is the start date for "${nameFromIntent}"? (YYYY-MM-DD)`);
        speak(`What is the start date for ${nameFromIntent}?`);
        return;
    }

    setPendingHoliday({});
    pushAIChat("Sure! What should the holiday be named?");
    speak("What should the holiday be named?");
};


// -------------------------------
// CREATE HOLIDAY — PROCESS
// -------------------------------
export const processPendingHoliday = (
    text: string,
    pending: PendingHoliday,
    setPendingHoliday: (data: PendingHoliday | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
       // STEP 1 — name
    if (!pending.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid holiday name.");
            speak("Please say a valid holiday name.");
            return;
        }

        setPendingHoliday({ ...pending, name: cleanName });

        pushAIChat(`Great! What is the start date for "${cleanName}"? (YYYY-MM-DD)`);
        speak(`What is the start date for ${cleanName}?`);
        return;
    }

    // STEP 2 — start date
    if (!pending.startDate) {
        const extracted = text.match(/\d{4}-\d{2}-\d{2}/)?.[0];

        if (!extracted) {
            pushAIChat("Please provide a date in YYYY-MM-DD format.");
            speak("Please provide the date in year-month-day format.");
            return;
        }

        setPendingHoliday({ ...pending, startDate: extracted });

        pushAIChat(`Got it! What is the end date? (YYYY-MM-DD)`);
        speak("What is the end date?");
        return;
    }

    // STEP 3 — end date
    if (!pending.endDate) {
        const extracted = text.match(/\d{4}-\d{2}-\d{2}/)?.[0];

        if (!extracted) {
            pushAIChat("Please provide a valid end date in YYYY-MM-DD format.");
            speak("Please provide a valid end date.");
            return;
        }

        setPendingHoliday({ ...pending, endDate: extracted });

        pushAIChat("Great! Any description for this holiday?");
        speak("Any description?");
        return;
    }

    // STEP 4 — description
    if (!pending.description) {
        setPendingHoliday({ ...pending, description: text });

        pushAIChat(`Should this holiday be active or inactive?`);
        speak(`Should this holiday be active or inactive?`);
        return;
    }

    // STEP 5 — status
    if (!pending.status) {
        const status = text.includes("inactive") ? "inactive" : "active";

        setPendingHoliday({ ...pending, status });

        pushAIChat(`What type of holiday is this? (optional)`);
        speak(`What type of holiday is this?`);
        return;
    }

    // STEP 6 — holiday type
    if (!pending.holidayType) {
        const holidayType = text.trim();

        setPendingHoliday({ ...pending, holidayType });

        // FINALLY CREATE HOLIDAY
        pushAIChat(
            `Perfect! Creating holiday "${pending.name}" from ${pending.startDate} to ${pending.endDate}...`
        );
        speak(`Creating holiday ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-holiday", {
                detail: {
                    ...pending,
                    holidayType
                }
            })
        );

        setPendingHoliday(null);
        return;
    }
};


// -------------------------------
// DELETE HOLIDAY — START
// -------------------------------
export const startDeleteHolidayFlow = (
    setPendingDelete: (data: PendingDeleteHoliday | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting holiday "${nameFromIntent}"...`);
        speak(`Deleting holiday ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-holiday", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    // Ask user for holiday name
    setPendingDelete({ awaitingName: true });

    pushAIChat("Sure! Which holiday would you like to delete?");
    speak("Which holiday would you like to delete?");
};


// -------------------------------
// DELETE HOLIDAY — PROCESS
// -------------------------------
export const processPendingDeleteHoliday = (
    text: string,
    pending: PendingDeleteHoliday,
    setPendingDelete: (data: PendingDeleteHoliday | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please say a valid holiday name.");
            speak("Please provide a valid holiday name.");
            return;
        }

        pushAIChat(`Deleting holiday "${cleanName}"...`);
        speak(`Deleting holiday ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-holiday", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
        return;
    }
};
